import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axios';

const STATUS_CONFIG = {
  Confirmed: { color: 'text-green-700 bg-green-50 border-green-200', icon: 'CheckCircle2', iconColor: '#16a34a' },
  Pending: { color: 'text-amber-700 bg-amber-50 border-amber-200', icon: 'Clock', iconColor: '#d97706' },
  Denied: { color: 'text-red-700 bg-red-50 border-red-200', icon: 'XCircle', iconColor: '#dc2626' },
  'No Response': { color: 'text-gray-600 bg-gray-50 border-gray-200', icon: 'PhoneMissed', iconColor: '#6b7280' },
};

const PAGE_SIZE = 10;

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG?.[status] || STATUS_CONFIG?.['Pending'];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap ${cfg?.color}`}>
      <Icon name={cfg?.icon} size={11} color={cfg?.iconColor} />
      {status}
    </span>
  );
};

const SkeletonRow = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 7 })?.map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div className="h-4 bg-gray-100 rounded w-full" />
      </td>
    ))}
  </tr>
);

const SortIcon = ({ col, sortCol, sortDir }) => {
  if (sortCol !== col) return <Icon name="ChevronsUpDown" size={13} color="#d1d5db" />;
  return <Icon name={sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={13} color="#3b82f6" />;
};

const OCTable = ({ from, to, filters, refreshKey, onRowClick }) => {
  const [rows, setRows] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [sortCol, setSortCol] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const searchTimerRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Debounce search from filters
  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSearchQuery(filters?.search?.trim() || '');
      setPage(1);
    }, 400);
    return () => clearTimeout(searchTimerRef.current);
  }, [filters?.search]);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [filters?.confirmationStatus, from, to]);

  useEffect(() => {
    if (!from || !to) return;
    setLoading(true);
    const params = { from, to, page: page - 1, size: PAGE_SIZE };
    if (filters?.confirmationStatus && filters.confirmationStatus !== 'all') {
      params.status = filters.confirmationStatus;
    }
    if (searchQuery) params.search = searchQuery;

    api.get('/api/v1/order-confirmation/orders', { params })
      .then((res) => {
        if (res.data?.success && res.data?.data) {
          setRows(res.data.data.rows || []);
          setTotalOrders(res.data.data.totalOrders || 0);
        }
      })
      .catch((err) => console.error('Failed to fetch OC orders:', err))
      .finally(() => setLoading(false));
  }, [from, to, filters?.confirmationStatus, searchQuery, page, refreshKey]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  // Client-side sort on the current page data
  const sorted = [...rows].sort((a, b) => {
    let av, bv;
    if (sortCol === 'date') {
      av = a?.orderDate ? new Date(a.orderDate) : 0;
      bv = b?.orderDate ? new Date(b.orderDate) : 0;
    } else if (sortCol === 'value') {
      av = a?.totalAmount || 0;
      bv = b?.totalAmount || 0;
    } else if (sortCol === 'calls') {
      av = a?.callsAttempted || 0;
      bv = b?.callsAttempted || 0;
    } else return 0;
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(totalOrders / PAGE_SIZE);

  const thClass = "px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap bg-gray-50";
  const sortableTh = "cursor-pointer select-none hover:text-blue-600 hover:bg-blue-50 transition-colors";

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return dateStr; }
  };

  if (!loading && sorted.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col items-center justify-center py-20 gap-4">
        <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center">
          <Icon name="PackageSearch" size={32} color="#d1d5db" />
        </div>
        <div className="text-center">
          <p className="text-base font-semibold text-gray-700">No order confirmations found</p>
          <p className="text-sm text-gray-400 mt-1">Try adjusting your filters or search terms</p>
        </div>
      </div>
    );
  }

  // Pagination helper
  const paginationPages = (() => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i <= 2 || i === totalPages || (i >= page - 1 && i <= page + 1)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '...') {
        pages.push('...');
      }
    }
    return pages;
  })();

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table style={{ minWidth: 900 }} className="w-full border-collapse">
          <thead>
            <tr className="border-b border-gray-100">
              <th className={`${thClass} ${sortableTh}`} onClick={() => handleSort('date')}>
                <span className="flex items-center gap-1">
                  Order Date <SortIcon col="date" sortCol={sortCol} sortDir={sortDir} />
                </span>
              </th>
              <th className={thClass}>Order ID</th>
              <th className={thClass}>Customer Name</th>
              <th className={thClass}>Mobile Number</th>
              <th className={`${thClass} ${sortableTh}`} onClick={() => handleSort('value')}>
                <span className="flex items-center gap-1">
                  Order Value <SortIcon col="value" sortCol={sortCol} sortDir={sortDir} />
                </span>
              </th>
              <th className={thClass}>Confirmation Status</th>
              <th className={`${thClass} ${sortableTh}`} onClick={() => handleSort('calls')}>
                <span className="flex items-center gap-1">
                  Calls Attempted <SortIcon col="calls" sortCol={sortCol} sortDir={sortDir} />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
              : sorted.map((row, idx) => (
                  <tr
                    key={row?.orderId || idx}
                    onClick={() => onRowClick?.(row)}
                    className={`border-b border-gray-50 cursor-pointer transition-colors hover:bg-blue-50/40 ${
                      idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                    }`}
                  >
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(row?.orderDate)}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-blue-600 whitespace-nowrap">{row?.orderId}</td>
                    <td className="px-4 py-3 text-sm text-gray-800 font-medium whitespace-nowrap">{row?.customerName || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{row?.phone || '—'}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-800 whitespace-nowrap">{row?.totalAmount?.toLocaleString()}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <StatusBadge status={row?.confirmationStatus || 'Pending'} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm font-semibold text-gray-700">{row?.callsAttempted || 0}</span>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalOrders)} of {totalOrders} orders
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition-colors"
            >
              <Icon name="ChevronLeft" size={16} color="#6b7280" />
            </button>
            {paginationPages.map((p, idx) =>
              p === '...' ? (
                <span key={`dots-${idx}`} className="w-7 h-7 flex items-center justify-center text-xs text-gray-400">...</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-7 h-7 rounded-lg text-xs font-semibold transition-colors ${
                    page === p ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {p}
                </button>
              )
            )}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition-colors"
            >
              <Icon name="ChevronRight" size={16} color="#6b7280" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OCTable;
